import {
  Button,
  HStack,
  Pressable,
  Select,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { useEffect, useState } from 'react';
import { Separator, useToken } from '@chakra-ui/react';
import { useUpdateMeetingStatus } from '../../../api/healthCoach/mutations/useUpdateMeetingStatus';
import { MeetingNote, PmStatus } from '../../../api/types';
import { ChevronDownIcon, ChevronUpIcon, ClockIcon } from 'lucide-react';

const durationValues = [
  '5',
  '10',
  '15',
  '20',
  '25',
  '30',
  '35',
  '40',
  '45',
  '50',
  '55',
  '60',
  '65',
  '70',
  '75',
  '80',
  '85',
  '90',
  '95',
  '100',
  '105',
  '110',
  '115',
  '120',
];

const statuses = [
  PmStatus.occurred,
  PmStatus.noShow,
  PmStatus.cancelled,
  PmStatus.lateCancelled,
];

type Props = {
  meeting: MeetingNote;
};

const MeetingCardEditor = ({ meeting }: Props) => {
  const { healthie_appointment_id: meetingId } = meeting;

  const [expanded, setExpanded] = useState<boolean>(false);
  const [status, setStatus] = useState<PmStatus>();
  const [duration, setDuration] = useState<string>();

  const { mutation: updateMeetingStatus } = useUpdateMeetingStatus();

  const [aqua] = useToken('colors', ['aqua']);

  useEffect(() => {
    setStatus(undefined);
    setDuration(undefined);
    setExpanded(false);
  }, [meetingId]);

  const onSubmit = async () => {
    if (!status) return;

    const payload = duration
      ? { pm_status: status, actual_duration: duration }
      : { pm_status: status };

    await updateMeetingStatus.mutateAsync({ meetingId, payload });
  };

  const selectStatus = (selectedOption: typeof statuses[number]) => {
    setStatus(selectedOption);

    if (selectedOption !== PmStatus.occurred) {
      setDuration(undefined);
      setExpanded(false);
    }
  };

  const statusSelectionComplete = status
    ? status !== PmStatus.occurred ||
      (status === PmStatus.occurred && !!duration)
    : false;

  return (
    <VStack>
      <Separator mt={4} mb={2} />
      <Pressable onClick={() => setExpanded(!expanded)}>
        <HStack w={'100%'} justifyContent={'space-between'}>
          <Text fontSize={'md'} fontWeight={600} color={'aqua'}>
            Mark Status
          </Text>
          {expanded ? (
            <ChevronUpIcon color={aqua} />
          ) : (
            <ChevronDownIcon color={aqua} />
          )}
        </HStack>
      </Pressable>
      {expanded ? (
        <VStack mt={8} mb={2} gap={2}>
          {statuses.map((statusOption) => (
            <>
              <Button
                key={statusOption}
                size={'sm'}
                variant={status === statusOption ? 'solid' : 'outline'}
                onClick={() => selectStatus(statusOption)}
              >
                {statusOption}
              </Button>

              {statusOption === PmStatus.occurred &&
                status === PmStatus.occurred && (
                  <Select
                    iconLeft={<ClockIcon size={16} />}
                    h={10}
                    my={2}
                    borderColor={'cloud.200'}
                    placeholder="Select Duration"
                    color={'secondary.200'}
                    selectedValue={duration}
                    onValueChange={(val) => {
                      setDuration(val);
                      setExpanded(false);
                    }}
                  >
                    {durationValues.map((item) => {
                      return (
                        <Select.Item key={item} label={item} value={item} />
                      );
                    })}
                  </Select>
                )}
            </>
          ))}
        </VStack>
      ) : (
        <VStack gap={8}>
          <Text color={'seafoam'} fontWeight={700} fontSize={'sm'}>
            {status}
            {duration && `, ${duration} mins`}
          </Text>
          {!!status && (
            <Button
              size={'sm'}
              disabled={!statusSelectionComplete}
              loading={updateMeetingStatus.isLoading}
              onClick={onSubmit}
            >
              Submit
            </Button>
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default MeetingCardEditor;
